import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/cn'
import type { MyAttendanceTodayCompanyEntry } from '@/features/attendance/types/attendance.types'
import { getLiveElapsedSeconds } from '@/features/attendance/utils/liveElapsed'

function formatHours(minutes: number): string {
  if (minutes <= 0) return '0h 0m'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h ${m}m`
}

interface RowDatum {
  key: string
  companyName: string
  shiftName: string | null
  minutes: number
  status: 'active' | 'break' | 'complete' | 'idle'
}

export interface AttendanceTodayTotalsProps {
  entries: MyAttendanceTodayCompanyEntry[]
  companyNameById: Map<string, string>
  serverTime?: string
}

export function AttendanceTodayTotals({
  entries,
  companyNameById,
  serverTime,
}: AttendanceTodayTotalsProps): React.ReactElement {
  const hasRunningShift = useMemo(
    () =>
      entries.some((entry) =>
        entry.shifts.some(
          (shift) => shift.attendance?.isActive && !shift.attendance?.isOnBreak,
        ),
      ),
    [entries],
  )
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    if (!hasRunningShift) return
    const interval = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [hasRunningShift])

  const rows = useMemo<RowDatum[]>(() => {
    const out: RowDatum[] = []
    for (const e of entries) {
      const companyName = companyNameById.get(e.companyId) ?? 'Company'
      for (const s of e.shifts) {
        const a = s.attendance
        const minutes = a
          ? a.isActive
            ? Math.floor(getLiveElapsedSeconds(a, nowMs, serverTime) / 60)
            : a.totalWorkedMinutes ?? 0
          : 0
        const status: RowDatum['status'] = a?.dayCompleted
          ? 'complete'
          : a?.isOnBreak
            ? 'break'
            : a?.isActive
              ? 'active'
              : 'idle'
        const shiftRef =
          s.assignment?.shiftId && typeof s.assignment.shiftId === 'object'
            ? (s.assignment.shiftId as { name?: string })
            : null
        out.push({
          key: s.shiftAssignmentId,
          companyName,
          shiftName: shiftRef?.name ?? null,
          minutes,
          status,
        })
      }
    }
    return out
  }, [companyNameById, entries, nowMs, serverTime])

  const totalMinutes = rows.reduce((sum, r) => sum + r.minutes, 0)
  const maxMinutes = Math.max(...rows.map((r) => r.minutes), 60)

  if (rows.length === 0 || totalMinutes === 0) {
    return (
      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Today&apos;s totals
          </span>
          <span className="rounded-full border border-gray-200 bg-white/85 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-400">
            0h 0m
          </span>
        </div>
        <div className="rounded-2xl border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,255,255,0.98))] p-4 dark:border-gray-700 dark:bg-[linear-gradient(180deg,rgba(31,41,55,0.68),rgba(31,41,55,0.92))]">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-100">
            No tracked time yet
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Totals will appear here once a shift starts or completed work is recorded.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Today&apos;s totals
        </span>
        <span className="rounded-full border border-gray-200 bg-white/85 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100">
          {formatHours(totalMinutes)}
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const widthPct = Math.round((r.minutes / maxMinutes) * 100)
          return (
            <div
              key={r.key}
              className="flex items-center gap-3 text-xs text-gray-700 dark:text-gray-300"
            >
              <span className="w-36 shrink-0 truncate font-medium text-gray-900 dark:text-gray-100">
                {r.companyName}
                {r.shiftName && (
                  <span className="text-gray-400 dark:text-gray-500">
                    {' '}
                    — {r.shiftName}
                  </span>
                )}
              </span>
              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/6">
                <div
                  className={cn(
                    'h-full rounded-full',
                    r.status === 'active' && 'bg-green-500',
                    r.status === 'break' && 'bg-amber-500',
                    r.status === 'complete' && 'bg-gray-400',
                    r.status === 'idle' && 'bg-gray-300',
                  )}
                  style={{ width: `${widthPct}%` }}
                  aria-hidden
                />
              </div>
              <span className="w-16 shrink-0 text-right tabular-nums">
                {formatHours(r.minutes)}
              </span>
              <span className="hidden w-16 shrink-0 text-right text-[11px] uppercase tracking-wide text-gray-400 sm:inline dark:text-gray-500">
                {r.status === 'active'
                  ? 'Active'
                  : r.status === 'break'
                    ? 'Break'
                    : r.status === 'complete'
                      ? 'Done'
                      : ''}
              </span>
            </div>
          )
        })}
        <div className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-600 dark:border-white/10 dark:text-gray-400">
          <span>Billable today: </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatHours(totalMinutes)}
          </span>
          <span className="ml-2 text-gray-400 dark:text-gray-500">
            (sum across shifts)
          </span>
        </div>
      </div>
    </section>
  )
}

AttendanceTodayTotals.displayName = 'AttendanceTodayTotals'
